import { CanvasTexture, SRGBColorSpace, type Texture } from 'three';
import type { PlanetPalette, PlanetTraits } from './planetTraits.js';

/**
 * A planet's skin: pole-to-equator shading, and faint banding on the gas
 * bodies. Painted once to a small canvas and used as a texture, which is a
 * great deal cheaper than a shader and reads the same at this distance.
 *
 * The skin is identity only. Health never touches it — it is carried by the
 * atmosphere and the label, so a world keeps its own colour even on its worst
 * day, and the worst day still shows.
 */
const WIDTH = 8;
const HEIGHT = 256;

function bandAlpha(seed: number, i: number): number {
  const t = Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453;
  return Math.abs(t - Math.floor(t));
}

/**
 * A soft radial glow, for the star's corona. A shell of geometry cannot do
 * this: however faint it is made, its edge is still an edge, and it reads as a
 * grey outline drawn around the sun.
 */
export function glowTexture(): Texture | null {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const glow = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  glow.addColorStop(0, 'rgba(255,255,255,0.85)');
  glow.addColorStop(0.28, 'rgba(255,255,255,0.28)');
  glow.addColorStop(0.6, 'rgba(255,255,255,0.06)');
  glow.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 128, 128);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

export function planetSkin(traits: PlanetTraits, seed: number): Texture | null {
  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  // jsdom has no 2D context, and a missing skin is only a plainer planet.
  if (!ctx) return null;

  const { palette } = traits;
  paintBody(ctx, palette);
  if (palette.band) paintBands(ctx, palette.band, seed);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  return texture;
}

function paintBody(ctx: CanvasRenderingContext2D, palette: PlanetPalette): void {
  // The texture runs pole to pole, so a vertical ramp puts the lighter,
  // colder colour at the caps the way an atmosphere thins towards them.
  const ramp = ctx.createLinearGradient(0, 0, 0, HEIGHT);
  ramp.addColorStop(0, palette.pole);
  ramp.addColorStop(0.32, palette.equator);
  ramp.addColorStop(0.68, palette.equator);
  ramp.addColorStop(1, palette.pole);
  ctx.fillStyle = ramp;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);
}

function paintBands(ctx: CanvasRenderingContext2D, band: string, seed: number): void {
  ctx.fillStyle = band;
  for (let i = 0; i < 9; i += 1) {
    const centre = bandAlpha(seed, i) * HEIGHT;
    const height = 3 + bandAlpha(seed, i + 40) * 14;
    ctx.globalAlpha = 0.1 + bandAlpha(seed, i + 80) * 0.22;
    ctx.fillRect(0, centre - height / 2, WIDTH, height);
  }
  ctx.globalAlpha = 1;
}
